<template>
    <div>
        <div :class="`mb-[20px]`">
            <div
                @click="toggleDropdown"
                class="flex items-center cursor-pointer justify-between transition-height-transform duration-200 delay-300">
                <div class="flex flex-col gap-2 flex-1">
                    <span class="font-semibold">{{ $t('search.categories') }}</span>
                    <!-- Display selected categories -->
                    <div v-if="selectedCategoryBadges.length > 0" class="flex flex-wrap gap-2 mt-2">
                        <div 
                            v-for="selectedCategory in selectedCategoryBadges" 
                            :key="selectedCategory.id"
                            class="flex items-center gap-2 bg-primary/10 border border-primary/20 rounded-lg px-3 py-1.5 text-sm hover:bg-primary/15 transition-colors duration-200 group"
                        >
                            <!-- Category name -->
                            <span class="text-primary font-medium text-xs">
                                {{ selectedCategory.name }}
                            </span>
                            
                            <!-- Parent name for sub categories -->
                            <span 
                                v-if="selectedCategory.type === 'sub_category' && selectedCategory.parentName" 
                                class="text-gray-500 text-xs opacity-75"
                            >
                                ({{ selectedCategory.parentName }})
                            </span>
                            
                            <!-- Remove button -->
                            <button 
                                @click.stop="removeCategory"
                                class="ml-1 text-gray-400 hover:text-red-500 transition-colors duration-200 p-0.5 rounded-full hover:bg-red-50 group-hover:bg-red-50"
                                :title="$t('general.remove')"
                            >
                                <svg class="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
                                </svg>
                            </button>
                        </div>
                    </div>
                </div>
                <span class="w-8 h-8 flex items-center justify-center cursor-pointer">
                    <img :class="` ${!isOpen ? ' rotate-90 ' : ' rotate-0 '} transition-height-transform  cursor-pointer`"
                        src="../../assets/icons/down.svg" alt="">
                </span>
            </div>
            <div :class="`${!isOpen ? 'min-h-[0px]' : 'min-h-[100px]'} transition-height-transform  text-[14px] text-[#484848BD]`">
                <div :class="`${!isOpen ? 'hidden' : 'block'} mt-[10px] `">
                    <!-- Add search input -->
                    <div class="mb-3">
                        <div class="relative">
                            <input 
                                v-model="searchInput"
                                class="w-full px-4 py-2 pr-10 text-sm border border-[#C6C6C6] rounded-md focus:outline-none focus:border-primary"
                                :placeholder="$t('search.search_categories')"
                                type="text"
                            >
                            <span class="absolute right-3 top-1/2 -translate-y-1/2">
                                <img src="../../assets/icons/search.svg" alt="" class="w-4 h-4 opacity-50">
                            </span>
                        </div>
                    </div>

                    <div class="flex items-center gap-[12px] mb-[10px] ">
                        <input 
                            type="checkbox" 
                            @change="selectAllCategories"
                            :checked="searchAllCategories"
                            ref="allCategoriesCheckbox"
                            class="border border-[#C6C6C6] cursor-pointer w-[20px] h-[20px]"
                        >
                        <div>
                            {{ $t('search.all_categories') }}
                        </div>
                    </div>
                    <div v-for="(category, idx) in filteredCategories" :key="idx">
                        <div class="flex items-center gap-[12px] mb-[10px] ">
                            <div @click="() => toggleCategory(category.id)"
                                class="cursor-pointer w-[20px] h-[20px] flex items-center justify-center rounded-sm border border-[#C6C6C6] bg-white text-[#7575758f]">
                                {{ category.isOpen ? '-' : '+' }}
                            </div>
                            <div>
                                {{ category?.name_ar }}
                            </div>
                        </div>
                        <div v-if="category?.isOpen">
                            <div v-for="(sub, index) in getFilteredSubCategories(category)" :key="index"
                                @click="selectCategory(sub.id)"
                                :class="` cursor-pointer pr-[22px] mr-[10px] border-r pb-[10px] ${sub.isActive ? 'text-primary' : 'hover:opacity-60'}`">
                                {{ sub.name_ar }}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import { getFilteredSubCategories } from '@/utils/filterUtils'

export default {
    name: 'FilterCategories',
    props: {
        categories: {
            type: Array,
            required: true,
            default: () => []
        },
        selectedCategoryId: {
            type: [Number, String],
            default: null
        },
        searchAllCategories: {
            type: Boolean,
            default: true
        }
    },
    emits: ['category-selected', 'category-toggled', 'remove-category'],
    data() {
        return {
            isOpen: false,
            searchInput: "",
        }
    },
    computed: {
        filteredCategories() {
            if (!this.searchInput) {
                return this.categories
            }
            
            const searchTerm = this.searchInput.toLowerCase()
            
            return this.categories.filter(category => {
                const matchesMainCategory = 
                    category.name_ar?.toLowerCase().includes(searchTerm) ||
                    category.name_en?.toLowerCase().includes(searchTerm)
                    
                const hasMatchingSubCategories = category.children?.some(sub => 
                    sub.name_ar?.toLowerCase().includes(searchTerm) ||
                    sub.name_en?.toLowerCase().includes(searchTerm)
                )
                
                return matchesMainCategory || hasMatchingSubCategories
            })
        },
        selectedCategoryBadges() {
            if (this.searchAllCategories || !this.selectedCategoryId) return []
            
            const selectedCategory = this.categories.find(category => {
                if (category.id === this.selectedCategoryId) return true
                if (category.children && Array.isArray(category.children)) {
                    return category.children.some(child => child.id === this.selectedCategoryId)
                }
                return false
            })
            
            if (!selectedCategory) return []
            
            if (selectedCategory.id === this.selectedCategoryId) {
                // Main category selected
                return [{
                    id: selectedCategory.id,
                    name: this.$i18n.locale === 'ar' ? selectedCategory.name_ar : selectedCategory.name_en,
                    type: 'main_category'
                }]
            } else {
                // Sub category selected
                const subCategory = selectedCategory.children.find(child => child.id === this.selectedCategoryId)
                if (subCategory) {
                    return [{
                        id: subCategory.id,
                        name: this.$i18n.locale === 'ar' ? subCategory.name_ar : subCategory.name_en,
                        type: 'sub_category',
                        parentName: this.$i18n.locale === 'ar' ? selectedCategory.name_ar : selectedCategory.name_en
                    }]
                }
            }
            
            return []
        }
    },
    methods: {
        toggleDropdown() {
            this.isOpen = !this.isOpen
        },
        toggleCategory(categoryId) {
            this.$emit('category-toggled', categoryId)
        },
        selectCategory(categoryId) {
            this.$emit('category-selected', categoryId)
        },
        selectAllCategories() {
            this.$emit('category-selected', null)
        },
        removeCategory() {
            this.$emit('remove-category')
        },
        getFilteredSubCategories(category) {
            return getFilteredSubCategories(category, this.searchInput)
        }
    }
}
</script>

<style scoped>
.transition-height-transform {
    transition-property: min-height, transform;
    transition-duration: 300ms;
    transition-timing-function: ease-in-out;
}
</style>

